import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import SearchProductPreviewComponent from "./SearchProductPreviewComponent";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InputInterface } from "../../../interfaces/InputInterface";
import { DEFAULT_NB_PRODUCT_SLIDER } from "../../../utils/FArticleUtils";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FArticleSmallInterface } from "../../../interfaces/FArticleInterface";
import Divider from "@mui/material/Divider";
import SearchProductFArticleSellingComponent from "./SearchProductFArticleSellingComponent";
import SearchProductTextComponent from "./SearchProductText/SearchProductTextComponent";

interface State {
  data: any;
  fArticle: FArticleSmallInterface | undefined;
}

interface FormState {
  nbProductsDisplay: InputInterface;
  url: InputInterface;
}

const SearchProductComponent = React.memo(
  forwardRef(({ data, fArticle }: State, ref) => {
    const fArticleSellingTypeRef: any = useRef();
    const textsRef: any = useRef();
    const { t } = useTranslation();

    const getValues = React.useCallback(() => {
      return {
        nbProductsDisplay: {
          value:
            data?.nbProductsDisplay && Number(data.nbProductsDisplay) > 0
              ? data.nbProductsDisplay
              : DEFAULT_NB_PRODUCT_SLIDER,
          error: "",
        },
        url: {
          value: data?.url ?? "",
          error: "",
        },
      };
    }, [data?.nbProductsDisplay, data?.url]);

    const [values, setValues] = React.useState<FormState>(getValues());

    const getValue = React.useCallback(() => {
      const result: any = {
        nbProductsDisplay:
          Number(values.nbProductsDisplay.value) ?? DEFAULT_NB_PRODUCT_SLIDER,
      };
      const fArticleSellingTypeRefValue =
        fArticleSellingTypeRef.current?.getValue();
      const textsRefValue = textsRef.current.getValue();
      if (textsRefValue !== null) {
        result.texts = textsRefValue;
      }
      result.url = values.url.value;
      if (typeof fArticleSellingTypeRefValue === "string") {
        if (!result.fArticle) {
          result.fArticle = {};
        }
        result.fArticle.fArticleSellingType = fArticleSellingTypeRefValue;
      }
      return result;
    }, [values.nbProductsDisplay.value, values.url.value]);

    const handleChange = React.useCallback(
      (prop: keyof FormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setValues((v) => {
            return {
              ...v,
              [prop]: { ...v[prop], value: event.target.value, error: "" },
            };
          });
        },
      []
    );

    useImperativeHandle(ref, () => ({
      getValue() {
        return getValue();
      },
    }));

    useEffect(() => {
      setValues(getValues());
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Box>
        <Grid sx={{ marginTop: 2 }} container spacing={1}>
          <Grid item xs={12}>
            <Typography component="p" variant="h5" sx={{ textAlign: "center" }}>
              {t("word.display")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl sx={{ width: "100%" }} variant="outlined">
              <InputLabel>{t("field.nbProductsDisplay")}</InputLabel>
              <OutlinedInput
                error={!!values.nbProductsDisplay.error}
                type="number"
                className="center-input"
                value={values.nbProductsDisplay.value}
                onChange={handleChange("nbProductsDisplay")}
                label={t("field.nbProductsDisplay")}
              />
              {!!values.nbProductsDisplay.error && (
                <FormHelperText error>
                  {t(values.nbProductsDisplay.error ?? "")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid sx={{ marginTop: 2 }} container spacing={1}>
          <Grid item xs={12}>
            <Typography component="p" variant="h5" sx={{ textAlign: "center" }}>
              {t("word.text")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <SearchProductTextComponent data={data} ref={textsRef} />
          </Grid>
        </Grid>
        <Grid sx={{ marginTop: 2 }} container spacing={1}>
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <TextField
              fullWidth={true}
              autoComplete="off"
              error={!!values.url.error}
              helperText={t(values.url.error ?? "")}
              sx={{ width: "100%" }}
              required
              type="text"
              value={values.url.value}
              onChange={handleChange("url")}
              label={t("field.url")}
            />
          </Grid>
        </Grid>
        {fArticle && (
          <Grid sx={{ marginTop: 2 }} container spacing={1}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Typography component="p" variant="h5">
                {t("word.product")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchProductFArticleSellingComponent
                ref={fArticleSellingTypeRef}
                data={data}
              />
            </Grid>
          </Grid>
        )}
        <Grid sx={{ marginTop: 2 }} container spacing={1}>
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <SearchProductPreviewComponent
              getValue={getValue}
              fArticle={fArticle}
            />
          </Grid>
        </Grid>
      </Box>
    );
  })
);

export default SearchProductComponent;
